import { ProfileStatusEnum } from '@zaoblako/shared/modules/security/enums/profile-status.enum';
import { SecurityDtoKeysEnum } from '@zaoblako/shared/modules/security/enums/security-dto-keys.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { FileFindDto, type FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';

import * as yup from 'yup';

/** Profile response body */
export interface ProfileFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Profile creation date and time */
  created: Date;
  /** Profile last update date and time */
  updated: Date;
  /** Profile soft deletion date and time (`null` for not deleted profiles) */
  deleted: Date;
  /** Profile status ({@link ProfileStatusEnum.INACTIVE} by default) */
  status?: ProfileStatusEnum;
  /** User email address (login) */
  email: string;
  /** User's Telegram identifier to use Telegram Bot */
  telegramId: string;
  /** User's phone number */
  phone: string;
  /** User's first (personal) name */
  firstName: string;
  /** User's middle (additional) name or surname */
  middleName: string;
  /** User's last (family) name */
  lastName: string;
  /** User's full name. This value will be generated automatically, using values from other name columns. */
  name: string;
  /** User's last visit date and time */
  lastTime: Date;
  /** Super-admin profile flag. Profiles with this flag has higher access level to some APIs. */
  isSuperAdmin?: boolean;
  /** User's avatar */
  file?: FileFindInterface;
  /** Telegram Bot invitation URL. This is a read-only property which calculates automatically. */
  telegramInviteUrl?: string;
  /** Shortcut for `Boolean(telegramId)`. This is a read-only property which calculates automatically. */
  isInvitedToTelegram?: boolean;
}

/** Profile response body - DTO schema */
export const ProfileFindDto = SerdeDtoClass.create<ProfileFindInterface>(SecurityDtoKeysEnum.PROFILE_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Дата создания',
    serializer: yup.date(),
  },
  updated: {
    label: 'Дата последнего изменения',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  status: {
    optional: true,
    default: ProfileStatusEnum.INACTIVE,
    label: 'Статус',
    serializer: yup
      .string()
      .oneOf(Object.values(ProfileStatusEnum)),
  },
  email: {
    sortable: true,
    label: 'Email',
    serializer: yup.string(),
  },
  telegramId: {
    label: 'Привязка к Telegram',
    serializer: yup.string(),
  },
  phone: {
    sortable: true,
    label: 'Телефон',
    serializer: yup.string(),
  },
  firstName: {
    label: 'Имя',
    serializer: yup.string(),
  },
  middleName: {
    label: 'Отчество',
    serializer: yup.string(),
  },
  lastName: {
    label: 'Фамилия',
    serializer: yup.string(),
  },
  name: {
    sortable: true,
    label: 'ФИО',
    serializer: yup.string(),
  },
  lastTime: {
    label: 'Последняя активность',
    serializer: yup.date(),
  },
  isSuperAdmin: {
    optional: true,
    label: 'Суперпользователь',
    hint: 'Флаг со специальными полномочиями. Только для технических специалистов ФРМ.',
    serializer: yup.boolean(),
  },
  file: {
    optional: true,
    label: 'Аватар',
    nested: FileFindDto,
    serializer: (_, nested) => nested,
  },
  telegramInviteUrl: {
    optional: true,
    label: 'Ссылка-приглашение в Telegram-бот',
    serializer: yup.string(),
  },
  isInvitedToTelegram: {
    sortable: true,
    optional: true,
    label: 'Воспользовался ли приглашением в Telegram-бот',
    serializer: yup.boolean(),
  },
});
